import { action, makeObservable, observable } from 'mobx';
import { plainToInstance } from 'class-transformer';
import { api } from '~/tools/api/api';
import { EApiUrl } from '~/tools/api/enums';
import { AxiosResponse } from 'axios';
import ReactGA from 'react-ga4';
import { ModelUnityUser } from '~/models/model.unity.user';
import { ModelUser } from '~/models/model.user';
import { ModelUserProfile } from '~/models/model.user.profile';
import { GaCategories, GaEvents } from '~/views/enums';
import { extractServerError } from '~/tools/error';

declare global {
    interface Window {
        helpshiftConfig: any;
        Helpshift?: any;
    }
}

export class AuthService {
    @observable public currentUser: ModelUser | null;
    @observable public userProfiles: ModelUserProfile[] = [];
    @observable public userToken: string | null = null;
    @observable public showUnityBanner = true;
    @observable public showBanner = false;
    @observable public showBalance = false;
    @observable public loading = true;
    @observable public redirectToHome = false;
    @observable public showForbiddenPopup = false;
    @observable public gaInitialized = false;
    @observable public lastError: { description: string; code?: string } = { description: '', code: undefined };

    constructor() {
        makeObservable(this);
    }

    @action
    initUser = async () => {
        this.clearLastError();
        const gaId = process.env.GA_ID;

        if (gaId && !this.gaInitialized) {
            this.gaInitialized = true;
            ReactGA.initialize(gaId);
        }

        const userLoggedIn = await this.getUserAuthStatus();
        console.info('User logged in', userLoggedIn);

        //const userToken: any = Cookies.get('userToken');
        if (userLoggedIn === true) {
            await this.getUser();
            await this.getUserBalance();
            await this.getUnityUser();
            return;
        }

        const userToken: any = window.sessionStorage.getItem('userToken');
        if (userToken) {
            await this.getUser(userToken);
            await this.getUserBalance();
            await this.getUnityUser();
        } else {
            this.showBanner = true;
            this.setRedirectToHome(true);
            this.setLoading(false);
        }
    };

    @action
    getUserAuthStatus = async () => {
        this.clearLastError();
        try {
            const response: AxiosResponse = await api.get(EApiUrl.authStatus);

            if (response.status === 200) {
                return true;
            }
        } catch (error: any) {
            this.setLastError(extractServerError(error?.response?.data ?? error));
            if (error.response && error.response.status && error.response.status === 403) {
                return false;
            }

            return false;
        }
    };

    @action
    logoutUserOnServer = async () => {
        this.clearLastError();
        try {
            const response: AxiosResponse = await api.get(EApiUrl.logout);

            if (response.status === 200) {
                return true;
            }
        } catch (error: any) {
            this.setLastError(extractServerError(error?.response?.data ?? error));
        }
    };

    @action
    getUnityUser = async () => {
        this.clearLastError();
        if (this.currentUser?.token) {
            try {
                const response: AxiosResponse = await api.get<ModelUnityUser>(
                    EApiUrl.unityUserSummary,
                    {
                        headers: {
                            Authorization: this.currentUser?.token,
                        },
                    },
                    true,
                );

                if (response.status === 200 && response.data.summary) {
                    this.setUnityData(plainToInstance(ModelUnityUser, response.data));
                }
            } catch (error: any) {
                this.setLastError(extractServerError(error?.response?.data ?? error));
            }

            window.helpshiftConfig.userId = this.currentUser?.uid;
            window.helpshiftConfig.userName = this.currentUser?.displayName;
            window.Helpshift('updateHelpshiftConfig');
        }
    };

    @action
    getUser = async (token = null) => {
        this.clearLastError();
        try {
            const response: AxiosResponse = await api.get<ModelUser>(EApiUrl.profile, {
                headers: {
                    Authorization: token,
                },
            });

            if (response.status === 200) {
                response.data.token = token ?? 'cookie-hack';
                response.data.isUnityLinked = false;
                if (response.data.dateOfBirth) {
                    const dateArr: Array<string> = response.data.dateOfBirth.split('-');
                    const year: number = dateArr[0] as unknown as number;
                    const month: number = dateArr[1] as unknown as number;
                    const day: number = dateArr[2] as unknown as number;
                    response.data.dateOfBirth = `${month}/${day}/${year}`;
                }

                this.setCurrentUser(plainToInstance(ModelUser, response.data));
                this.setLoading(false);
            }
        } catch (error: any) {
            this.setLastError(extractServerError(error?.response?.data ?? error));
            if (error.response && error.response.status && error.response.status === 403) {
                this.setShowForbiddenPopup(true);
            }
        }
    };

    @action
    getUserBalance = async () => {
        this.clearLastError();
        if (this.currentUser?.token) {
            try {
                const response: AxiosResponse = await api.get<ModelUnityUser>(EApiUrl.userBalance, {
                    headers: {
                        Authorization: this.currentUser?.token,
                    },
                });

                if (response.status === 200) {
                    this.currentUser.balance = +response.data.wdollars ? Math.floor(+response.data.wdollars) : 0;
                    this.currentUser.freePlay = +response.data.freeplay ? Math.floor(+response.data.freeplay) : 0;
                    this.setShowBalance(true);
                }
            } catch (error: any) {
                this.setLastError(extractServerError(error?.response?.data ?? error));
                if (error.response && error.response.status && error.response.status === 403) {
                    this.setShowForbiddenPopup(true);
                }
            }
        }
    };

    @action
    setLastError = (err: { description: string; code?: string }) => {
        this.lastError = err;
    };
    @action
    clearLastError = () => {
        this.lastError = { description: '', code: undefined };
    };

    @action
    setShowBalance = (value: boolean) => {
        this.showBalance = value;
    };

    @action
    setLoading = (value: boolean) => {
        this.loading = value;
    };

    @action
    setUserProfiles = (profiles: ModelUserProfile[]) => {
        this.userProfiles = profiles;
    };

    @action
    getUserProfiles = () => {
        return this.userProfiles;
    };

    @action
    setUserToken = (token: string | null) => {
        this.userToken = token;
    };

    @action
    getUserToken = () => {
        return this.userToken;
    };

    @action
    setRedirectToHome = (value: boolean) => {
        this.redirectToHome = value;
    };

    @action
    setShowForbiddenPopup = (value: boolean) => {
        this.showForbiddenPopup = value;
    };

    @action
    login = async (token: string) => {
        this.clearLastError();
        try {
            const response: AxiosResponse = await api.get<ModelUser>(EApiUrl.profile, {
                headers: {
                    Authorization: token,
                },
            });

            if (response.status === 200) {
                this.sendGaEvent({
                    category: GaCategories.loginFlow,
                    action: GaEvents.loginSuccess,
                    label: JSON.stringify({
                        user_id: response.data?.uid,
                        login_method: 'sms',
                        source: 'webshop_direct',
                    }),
                });
                response.data.token = token;
                /*const date = new Date();
          date.setTime(date.getTime() + (24 * 60 * 60 * 1000));

          this.setUserSession({name: 'userToken', token}, {path: '/', expires: date});*/
                this.setUserSession(token);
                this.setCurrentUser(plainToInstance(ModelUser, response.data));
                this.setUserProfiles([]);
                this.setUserToken(null);
                this.setLoading(false);
                await this.getUserBalance();
                this.getUnityUser();
                this.setRedirectToHome(false);
            }
        } catch (error: any) {
            this.setLastError(extractServerError(error?.response?.data ?? error));
        }
    };

    @action
    logout = async () => {
        //Cookies.remove('userToken');
        await this.logoutUserOnServer();
        window.sessionStorage.removeItem('userToken');
        this.setCurrentUser(null);
        this.showUnityBanner = true;
        this.setRedirectToHome(true);
        this.setShowBalance(false);
        this.clearLastError();
        window.helpshiftConfig.userId = this.currentUser?.uid;
        window.helpshiftConfig.userName = this.currentUser?.displayName;
        window.Helpshift('updateHelpshiftConfig');
    };

    @action
    setCurrentUser = (user: ModelUser | null) => {
        this.currentUser = user;
    };

    @action
    setUnityData = (unityUser: ModelUnityUser | null) => {
        if (this.currentUser && unityUser?.status?.loginStatus && unityUser?.status?.loginStatus === 'logged_in') {
            this.showUnityBanner = false;
            this.currentUser.totalUnityPoints = unityUser.summary.totalUnityPoints;
            this.currentUser.unityAccountNumber = unityUser.status.accountNumber;
            this.currentUser.firstName = unityUser.summary.firstName;
            this.currentUser.lastName = unityUser.summary.lastName;
            this.currentUser.email = unityUser.summary.emailAddress;
            this.currentUser.unityTier = unityUser.status.unityTier;
            this.currentUser.totalTierCredits = unityUser.summary.totalTierCredits;
        }

        this.showBanner = true;
    };

    /*setUserSession = (userSession: IUserSession, options?: CookieAttributes) => {
      Cookies.set(userSession.name, userSession.token, options);
    }*/
    setUserSession = (token: string) => {
        window.sessionStorage.setItem('userToken', token);
    };

    sendGaEvent = ({
        category,
        action,
        label,
        value,
    }: {
        category: string;
        action: string;
        label?: string;
        value?: number;
    }) => {
        ReactGA.event({ category, action, label, value });
    };
}
